import { Component, EventEmitter, forwardRef, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface SelectItem {
  label: string;
  value?: any;
  backgroundColor?: string;
  labelColor?: string;
  useChip?: boolean;
  disabled?: boolean;
  hidden?: boolean;
}

@Component({
  selector: 'kt-dropdown-single-select',
  templateUrl: './dropdown-single-select.component.html',
  styleUrls: ['./dropdown-single-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DropdownSingleSelectComponent),
      multi: true
    }
  ]
})

export class DropdownSingleSelectComponent implements OnInit {
  @Input() heading: string;
  @Input() items: SelectItem[] = [];
  @Input() disabled: boolean;
  @Input() selectedOptionIndex: number | null = null;
  @Output() selectedItem = new EventEmitter<SelectItem | null>();
  @Input() refreshTrigger: any;
  @Input() bordered: boolean = false;
  @Input() placeholder: string = 'Select Status';
  @Input() searchable: boolean = false;
  @Input() searchPlaceholder: string = 'Search...';
  @Input() noEntriesFoundLabel: string = 'No entries found';

  form: UntypedFormGroup;
  searchFilter = new UntypedFormControl('');
  filteredItems: SelectItem[] = [];
  selectedText: string = '';
  selectedItemData: SelectItem | null = null;

  private onChange: (value: any) => void = () => { };
  private onTouched: () => void = () => { };

  constructor() {
    this.form = new UntypedFormGroup({
      item: new UntypedFormControl(null)
    });
  }

  ngOnInit() {
    this._updateFilteredItems();
    this.searchFilter.valueChanges.subscribe(() => this._updateFilteredItems());
    this._applySelectedOptionIndex();
    this.form.get('item')?.valueChanges.subscribe(value => {
      this.emitSelection(value);
      const emitValue = value?.value !== undefined ? value.value : value;
      this.onChange(emitValue);
      this.onTouched();
    });
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  // REQUIRED: Register the onTouched callback
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }


  ngOnChanges(changes: SimpleChanges) {
    if (changes?.items || changes?.searchable) {
      this._updateFilteredItems();
    }
    if (changes?.selectedOptionIndex || changes?.refreshTrigger || changes?.items) {
      this._applySelectedOptionIndex();
    }
  }

  get displayItems(): SelectItem[] {
    return this.searchable ? this.filteredItems : this.items;
  }

  private _updateFilteredItems(): void {
    const items = this.items ?? [];
    if (!this.searchable) {
      this.filteredItems = items;
      return;
    }
    const term = (this.searchFilter.value ?? '').toString().trim().toLowerCase();
    if (!term) {
      this.filteredItems = items;
      return;
    }
    this.filteredItems = items.filter((item) =>
      item.label?.toLowerCase().includes(term)
    );
  }

  private _applySelectedOptionIndex(): void {
    if (this.selectedOptionIndex == null || this.selectedOptionIndex < 0) {
      this.form.get('item')?.setValue(null, { emitEvent: false });
      this.selectedItemData = null;
      this.selectedText = '';
      return;
    }
    const initialValue = this.items?.[this.selectedOptionIndex];
    if (!initialValue) {
      this.form.get('item')?.setValue(null, { emitEvent: false });
      this.selectedItemData = null;
      this.selectedText = '';
      return;
    }
    this.form.get('item')?.setValue(initialValue, { emitEvent: false });
    this.selectedItemData = initialValue;
    this.selectedText = initialValue.label;
  }
  writeValue(value: any): void {
    const item = this.items.find(i => i.value === value) || null;
    this.form.get('item')?.setValue(item, { emitEvent: false });
    this.selectedItemData = item;
    this.selectedText = item ? item.label : '';
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.form.get('item')?.disable({ emitEvent: false });
    } else {
      this.form.get('item')?.enable({ emitEvent: false });
    }
  }

  emitSelection(selectedValue: SelectItem | null) {
  this.selectedItemData = null;
  // Use setTimeout to ensure the change is detected
  setTimeout(() => {
    this.selectedItemData = selectedValue;
    this.selectedItem.emit(selectedValue);
    this.selectedText = selectedValue ? selectedValue.label : '';
  }, 0);
  }
}